import type { LineKey } from "../domain/lines";
import { velocityDetuneRatio } from "./detune";
import { TIME_CONSTANT_S, stopOscillator } from "./graph";
import { PINK_NOISE_PROCESSOR_NAME, pinkNoiseModuleUrl } from "./pinkNoiseWorklet";

/** How one emission line is voiced: loudness, width, and peak velocity offset. */
export interface LineVoiceParams {
  /** Line flux relative to the object's bright state; 0 when soloed out. */
  strength: number;
  fwhmKms: number;
  /** Peak offset from rest, km/s (0 when the object has no kinematics). */
  offsetKms: number;
}

export interface AudioParameters {
  continuumBrightness: number;
  blueBalance: number;
  redBalance: number;
  lines: Record<LineKey, LineVoiceParams>;
  reddeningAv: number;
  layerGains: LayerGains;
}

type BalmerKey = Exclude<LineKey, "OIII">;
const BALMER_KEYS: readonly BalmerKey[] = ["Halpha", "Hbeta", "Hgamma"];

export interface LayerGains {
  continuum: number;
  balmer: number;
  oiii: number;
  dust: number;
}

/**
 * One item per epoch passed to ``AudioEngine.updateStacked``. ``trimLinear``
 * already encodes per-epoch fader, solo, and any headroom factor; the engine
 * applies it on a dedicated gain node so the timbre mapping inside
 * ``parameters`` stays identical to single-epoch mode.
 */
export interface StackedItem {
  parameters: AudioParameters;
  trimLinear: number;
}

export const DEFAULT_LAYER_GAINS: LayerGains = {
  continuum: 0.6,
  balmer: 0.7,
  oiii: 0.5,
  dust: 0.8,
};

const FREQUENCIES = {
  continuumRed: 130.81,
  continuumGreen: 220.0,
  continuumBlue: 329.63,
  oiii: 880.0,
};

/** Balmer voice pitch and the FWHM each bandpass starts at before data arrives. */
const BALMER_VOICES: Record<BalmerKey, { hz: number; initialFwhmKms: number }> = {
  Halpha: { hz: 110.0, initialFwhmKms: 5000 },
  Hbeta: { hz: 196.0, initialFwhmKms: 5500 },
  Hgamma: { hz: 261.63, initialFwhmKms: 5500 },
};

class LineVoice {
  readonly baseFrequency: number;
  readonly noise: AudioWorkletNode;
  readonly bandpass: BiquadFilterNode;
  readonly tone: OscillatorNode;
  readonly toneGain: GainNode;
  readonly outputGain: GainNode;
  readonly noiseGain: GainNode;

  constructor(ctx: AudioContext, frequency: number, color: AudioNode, fwhmKms: number) {
    this.baseFrequency = frequency;
    this.noise = new AudioWorkletNode(ctx, PINK_NOISE_PROCESSOR_NAME);
    this.bandpass = ctx.createBiquadFilter();
    this.bandpass.type = "bandpass";
    this.bandpass.frequency.value = frequency;
    this.bandpass.Q.value = fwhmToQ(fwhmKms);

    this.noiseGain = ctx.createGain();
    this.noiseGain.gain.value = 1.5;

    this.tone = ctx.createOscillator();
    this.tone.type = "sine";
    this.tone.frequency.value = frequency;
    this.toneGain = ctx.createGain();
    this.toneGain.gain.value = 0.04;
    this.tone.start();

    this.outputGain = ctx.createGain();
    this.outputGain.gain.value = 0;

    this.noise.connect(this.noiseGain);
    this.noiseGain.connect(this.bandpass);
    this.bandpass.connect(this.outputGain);
    this.tone.connect(this.toneGain);
    this.toneGain.connect(this.outputGain);

    this.outputGain.connect(color);
  }

  /** Glide loudness, bandwidth, and pitch (redward offset = lower) to the new line shape. */
  apply(line: LineVoiceParams, atTime: number): void {
    this.outputGain.gain.setTargetAtTime(Math.max(0, line.strength), atTime, TIME_CONSTANT_S);
    this.bandpass.Q.setTargetAtTime(fwhmToQ(line.fwhmKms), atTime, TIME_CONSTANT_S);
    const frequency = this.baseFrequency / velocityDetuneRatio(line.offsetKms);
    this.bandpass.frequency.setTargetAtTime(frequency, atTime, TIME_CONSTANT_S);
    this.tone.frequency.setTargetAtTime(frequency, atTime, TIME_CONSTANT_S);
  }

  dispose(): void {
    stopOscillator(this.tone);
    this.tone.disconnect();
    this.toneGain.disconnect();
    this.noise.disconnect();
    this.noiseGain.disconnect();
    this.bandpass.disconnect();
    this.outputGain.disconnect();
  }
}

function fwhmToQ(fwhmKms: number): number {
  return Math.max(0.2, Math.min(8, 5500 / Math.max(800, fwhmKms)));
}

/**
 * One full sonification voice — continuum colour trio, three Balmer line
 * voices, and the [O III] anchor — terminating in a dedicated trim gain so
 * an arbitrary number of branches can sum into the master bus with
 * independent levels and solo/mute behaviour.
 */
class EpochBranch {
  private readonly continuumGain: GainNode;
  private readonly redGain: GainNode;
  private readonly greenGain: GainNode;
  private readonly blueGain: GainNode;
  private readonly balmerBus: GainNode;
  private readonly oiiiBus: GainNode;
  private readonly oiiiGain: GainNode;
  private readonly balmer: Record<BalmerKey, LineVoice>;
  private readonly trim: GainNode;
  private readonly continuumOscs: OscillatorNode[] = [];
  private readonly oiiiOsc: OscillatorNode;

  constructor(ctx: AudioContext, output: AudioNode) {
    this.trim = ctx.createGain();
    this.trim.gain.value = 0;
    this.trim.connect(output);

    this.continuumGain = ctx.createGain();
    this.continuumGain.gain.value = DEFAULT_LAYER_GAINS.continuum;
    this.continuumGain.connect(this.trim);

    const buildOsc = (freq: number, type: OscillatorType): GainNode => {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      osc.connect(gain);
      gain.connect(this.continuumGain);
      osc.start();
      this.continuumOscs.push(osc);
      return gain;
    };

    this.redGain = buildOsc(FREQUENCIES.continuumRed, "sine");
    this.greenGain = buildOsc(FREQUENCIES.continuumGreen, "sine");
    this.blueGain = buildOsc(FREQUENCIES.continuumBlue, "triangle");

    this.balmerBus = ctx.createGain();
    this.balmerBus.gain.value = DEFAULT_LAYER_GAINS.balmer;
    this.balmerBus.connect(this.trim);

    this.balmer = {} as Record<BalmerKey, LineVoice>;
    for (const key of BALMER_KEYS) {
      const { hz, initialFwhmKms } = BALMER_VOICES[key];
      this.balmer[key] = new LineVoice(ctx, hz, this.balmerBus, initialFwhmKms);
    }

    this.oiiiBus = ctx.createGain();
    this.oiiiBus.gain.value = DEFAULT_LAYER_GAINS.oiii;
    this.oiiiBus.connect(this.trim);

    this.oiiiOsc = ctx.createOscillator();
    this.oiiiOsc.type = "triangle";
    this.oiiiOsc.frequency.value = FREQUENCIES.oiii;
    this.oiiiGain = ctx.createGain();
    this.oiiiGain.gain.value = 0;
    this.oiiiOsc.connect(this.oiiiGain);
    this.oiiiGain.connect(this.oiiiBus);
    this.oiiiOsc.start();
  }

  applyParameters(p: AudioParameters, atTime: number): void {
    this.continuumGain.gain.setTargetAtTime(
      p.layerGains.continuum * Math.min(1.5, p.continuumBrightness),
      atTime,
      TIME_CONSTANT_S,
    );
    this.balmerBus.gain.setTargetAtTime(p.layerGains.balmer, atTime, TIME_CONSTANT_S);
    this.oiiiBus.gain.setTargetAtTime(p.layerGains.oiii, atTime, TIME_CONSTANT_S);

    const redBalance = Math.max(0, p.redBalance);
    const blueBalance = Math.max(0, p.blueBalance);
    const greenBalance = 1.0;
    const total = redBalance + blueBalance + greenBalance;
    const norm = total === 0 ? 1 : total;
    this.redGain.gain.setTargetAtTime(0.18 * (redBalance / norm), atTime, TIME_CONSTANT_S);
    this.greenGain.gain.setTargetAtTime(0.18 * (greenBalance / norm), atTime, TIME_CONSTANT_S);
    this.blueGain.gain.setTargetAtTime(0.22 * (blueBalance / norm), atTime, TIME_CONSTANT_S);

    for (const key of BALMER_KEYS) this.balmer[key].apply(p.lines[key], atTime);
    this.oiiiGain.gain.setTargetAtTime(0.16 * p.lines.OIII.strength, atTime, TIME_CONSTANT_S);
  }

  setTrim(value: number, atTime: number): void {
    this.trim.gain.setTargetAtTime(Math.max(0, value), atTime, TIME_CONSTANT_S);
  }

  dispose(): void {
    for (const osc of this.continuumOscs) {
      stopOscillator(osc);
      osc.disconnect();
    }
    stopOscillator(this.oiiiOsc);
    this.oiiiOsc.disconnect();
    for (const key of BALMER_KEYS) this.balmer[key].dispose();
    this.continuumGain.disconnect();
    this.redGain.disconnect();
    this.greenGain.disconnect();
    this.blueGain.disconnect();
    this.balmerBus.disconnect();
    this.oiiiBus.disconnect();
    this.oiiiGain.disconnect();
    this.trim.disconnect();
  }
}

/**
 * The classic drone engine: a fixed voice per Balmer line plus continuum trio
 * and [O III] anchor, driven by catalogue measurements. Terminates in a trim
 * gain so the owning ``Sonifier`` can crossfade it against the feature engine.
 */
export class AudioEngine {
  private readonly ctx: AudioContext;
  private readonly trim: GainNode;
  private readonly dustFilter: BiquadFilterNode;
  private readonly singleBranch: EpochBranch;
  private stackedBranches: EpochBranch[] = [];
  private mode: "single" | "stacked" = "single";

  private constructor(ctx: AudioContext, output: AudioNode) {
    this.ctx = ctx;
    this.trim = ctx.createGain();
    this.trim.gain.value = 0;
    this.dustFilter = ctx.createBiquadFilter();
    this.dustFilter.type = "lowpass";
    this.dustFilter.frequency.value = 8000;
    this.dustFilter.Q.value = 0.7;
    this.trim.connect(this.dustFilter);
    this.dustFilter.connect(output);

    this.singleBranch = new EpochBranch(ctx, this.trim);
    this.singleBranch.setTrim(1, ctx.currentTime);
  }

  /** Load the pink-noise worklet the line voices need, then build the graph. */
  static async create(ctx: AudioContext, output: AudioNode): Promise<AudioEngine> {
    await ctx.audioWorklet.addModule(pinkNoiseModuleUrl());
    return new AudioEngine(ctx, output);
  }

  setActive(active: boolean): void {
    this.trim.gain.setTargetAtTime(active ? 1 : 0, this.ctx.currentTime, TIME_CONSTANT_S);
  }

  /** Single-epoch update path (observed / interpolated / reddening modes). */
  update(parameters: AudioParameters): void {
    const now = this.ctx.currentTime;
    if (this.mode !== "single") {
      for (const b of this.stackedBranches) b.setTrim(0, now);
      this.singleBranch.setTrim(1, now);
      this.mode = "single";
    }
    this.singleBranch.applyParameters(parameters, now);
    this.applyDust(parameters.reddeningAv, parameters.layerGains.dust, now);
  }

  /**
   * Stacked update path — one branch per epoch, summed into the same master.
   * Caller computes per-epoch ``trimLinear`` already including any solo
   * masking and headroom so the engine just applies it on the trim node.
   */
  updateStacked(items: StackedItem[], layerGains: LayerGains): void {
    const now = this.ctx.currentTime;
    this.ensureStackedBranches(items.length);
    if (this.mode !== "stacked") {
      this.singleBranch.setTrim(0, now);
      this.mode = "stacked";
    }
    for (let i = 0; i < items.length; i += 1) {
      this.stackedBranches[i].applyParameters(items[i].parameters, now);
      this.stackedBranches[i].setTrim(items[i].trimLinear, now);
    }
    this.applyDust(0, layerGains.dust, now);
  }

  private ensureStackedBranches(count: number): void {
    if (this.stackedBranches.length === count) return;
    const now = this.ctx.currentTime;
    for (const b of this.stackedBranches) b.dispose();
    this.stackedBranches = [];
    for (let i = 0; i < count; i += 1) {
      const branch = new EpochBranch(this.ctx, this.trim);
      branch.setTrim(0, now);
      this.stackedBranches.push(branch);
    }
  }

  private applyDust(av: number, dustWeight: number, atTime: number): void {
    const cutoff = 9000 * Math.pow(10, -0.45 * av);
    const w = Math.min(1, dustWeight);
    const finalCutoff = 9000 * (1 - w) + cutoff * w;
    this.dustFilter.frequency.setTargetAtTime(
      Math.max(180, finalCutoff),
      atTime,
      TIME_CONSTANT_S,
    );
  }
}
